// App.js
import React, { useState, useRef, useEffect } from "react";
import "./App.css";

/* 
  Helper: Compound‐Interest Calculation
  P: principal (number), r: annual rate (number), t: years (number), 
  n: compoundings per year (number), C: monthly contribution (number)
*/
const calculateCompoundInterest = (P, r, t, n, C) => {
  let data = [];
  for (let i = 0; i <= t; i += 0.1) {
    const principalGrowth = P * Math.pow(1 + r / n, n * i);
    const contributionsGrowth =
      C * ((Math.pow(1 + r / 12, 12 * i) - 1) / (r / 12));
    data.push({ x: i, y: principalGrowth + contributionsGrowth });
  }
  return data;
};

/* 
  1) CompoundInterestGraph Component (SVG Graph)
     Includes monthly contributions in the calculation.
*/
const CompoundInterestGraph = ({
  principalStr,
  rateStr,
  yearsStr,
  compoundingsStr,
  monthlyContributionStr,
  title,
}) => {
  // Convert strings → numbers (default to 0 if parse fails)
  const P = Number(principalStr) || 0;
  const r = Number(rateStr) || 0;
  const t = Number(yearsStr) || 0;
  const n = Number(compoundingsStr) || 1;
  const C = Number(monthlyContributionStr) || 0;

  const graphData = calculateCompoundInterest(P, r, t, n, C);
  const xMax = t;
  const yMax = graphData.reduce((max, point) => (point.y > max ? point.y : max), 0);
  const finalAmount = graphData[graphData.length - 1]?.y.toFixed(2) || "0.00";

  return (
    <div className="graph-container card">
      <h3 className="graph-title">{title}</h3>
      <svg width="700" height="350" viewBox="-50 -50 600 400">
        {/* Vertical grid lines */}
        {[...Array(6)].map((_, i) => (
          <g key={`v-${i}`}>
            <line x1={i * 100} y1="0" x2={i * 100} y2="300" stroke="#ddd" />
            <text x={i * 100} y="320" fontSize="12" textAnchor="middle">
              {Math.round((i / 5) * xMax)}
            </text>
          </g>
        ))}
        {/* Horizontal grid lines */}
        {[...Array(6)].map((_, i) => (
          <g key={`h-${i}`}>
            <line x1="0" y1={i * 50} x2="500" y2={i * 50} stroke="#ddd" />
            <text x="-20" y={300 - i * 50} fontSize="12" textAnchor="end">
              {Math.round((i / 5) * yMax)}
            </text>
          </g>
        ))}
        {/* Axes */}
        <line x1="0" y1="300" x2="500" y2="300" stroke="#333" strokeWidth="2" />
        <line x1="0" y1="0" x2="0" y2="300" stroke="#333" strokeWidth="2" />
        {/* Axis labels */}
        <text x="250" y="350" fontSize="14" textAnchor="middle">
          Years Invested
        </text>
        <text
          x="-70"
          y="100"
          fontSize="14"
          textAnchor="middle"
          transform="rotate(-90,-50,125)"
        >
          Total Savings ($)
        </text>
        <polyline
          fill="none"
          stroke="#FF5733"
          strokeWidth="3"
          points={graphData
            .map(({ x, y }) => `${(x / xMax) * 500},${300 - (y / yMax) * 300}`)
            .join(" ")}
        />
      </svg>
      <p className="final-amount">Final Savings: ${finalAmount}</p>
    </div>
  );
};

/* 
  2) DesmosGraph Component
     Collapsed expressions, includes monthly contributions.
*/
const DesmosGraph = ({
  principalStr,
  rateStr,
  yearsStr,
  compoundingsStr,
  monthlyContributionStr,
  title,
}) => {
  const calculatorRef = useRef(null);
  const calculator = useRef(null);
  const [showPopup, setShowPopup] = useState(false);

  // Convert strings → numbers
  const P = Number(principalStr) || 0;
  const r = Number(rateStr) || 0;
  const t = Number(yearsStr) || 0;
  const n = Number(compoundingsStr) || 1;
  const C = Number(monthlyContributionStr) || 0;

  useEffect(() => {
    if (!calculator.current && window.Desmos) {
      calculator.current = window.Desmos.GraphingCalculator(calculatorRef.current, {
        expressions: true,
        expressionsCollapsed: true,
        settingsMenu: false,
      });
      calculator.current.updateSettings({ expressionsCollapsed: true });
    }

    if (calculator.current) {
      const expression = `A(t)=${P}\\left(1+\\frac{${r}}{${n}}\\right)^{${n}t}+${C}\\left(\\frac{\\left(1+\\frac{${r}}{12}\\right)^{12t}-1}{${r}/12}\\right)`;
      calculator.current.setExpression({ id: "compound", latex: expression });

      const principalGrowth = P * Math.pow(1 + r / n, n * t);
      const contributionsGrowth =
        C * ((Math.pow(1 + r / 12, 12 * t) - 1) / (r / 12));
      const finalAmount = principalGrowth + contributionsGrowth;

      calculator.current.setMathBounds({
        left: 0,
        right: t <= 0 ? 10 : t,
        bottom: 0,
        top: finalAmount > 0 ? finalAmount * 1.1 : 1000,
      });
    }
  }, [P, r, t, n, C]);

  return (
    <div className="desmos-container card" style={{ marginTop: "30px", position: "relative" }}>
      {title && <h3 className="graph-title">{title}</h3>}
      <div className="desmos-info-icon" onClick={() => setShowPopup(true)} title="How to use the graph">
        ?
      </div>
      <div ref={calculatorRef} style={{ width: "900px", height: "450px", margin: "0 auto" }} />
      {showPopup && (
        <div className="popup-overlay" onClick={() => setShowPopup(false)}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <h4>How to Use the Graph</h4>
            <p>
              Drag your cursor over the graph to see the coordinates. The <strong>x-coordinate</strong> represents the time (in years) since you started investing, and the <strong>y-coordinate</strong> shows your total savings.
            </p>
            <button className="close-button" onClick={() => setShowPopup(false)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* 
  3) YearlyBreakdown Component 
     A table with the balance at the end of each year.
*/
const YearlyBreakdown = ({
  principalStr,
  rateStr,
  yearsStr,
  compoundingsStr,
  monthlyContributionStr,
}) => {
  const P = Number(principalStr) || 0;
  const r = Number(rateStr) || 0;
  const t = Number(yearsStr) || 0;
  const n = Number(compoundingsStr) || 1;
  const C = Number(monthlyContributionStr) || 0;

  const breakdown = [];
  for (let i = 0; i <= t; i++) {
    const principalGrowth = P * Math.pow(1 + r / n, n * i);
    const contributionsGrowth =
      C * ((Math.pow(1 + r / 12, 12 * i) - 1) / (r / 12));
    breakdown.push({
      year: i,
      principalGrowth,
      contributionsGrowth,
      total: principalGrowth + contributionsGrowth,
    });
  }

  return (
    <div className="yearly-breakdown card">
      <h3 className="breakdown-title">Year-by-Year Breakdown</h3>
      <table className="breakdown-table">
        <thead>
          <tr>
            <th>Year</th>
            <th>Principal Growth ($)</th>
            <th>Contributions ($)</th>
            <th>Total Savings ($)</th>
          </tr>
        </thead>
        <tbody>
          {breakdown.map((item) => (
            <tr key={item.year}>
              <td>{item.year}</td>
              <td>{item.principalGrowth.toFixed(2)}</td>
              <td>{item.contributionsGrowth.toFixed(2)}</td>
              <td>{item.total.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* 
  4) InfoPage Component 
     Shows detailed info about compound interest, real-life examples, etc.,
     plus new sections for Investment Platforms and Investment Vehicles.
*/
const InfoPage = () => {
  return (
    <div className="info-page card">
      <h2 className="info-title">The Power of Investing Early</h2>
      <div className="info-container">
        <div className="info-box">
          <h3 className="info-subtitle">Why Investing Matters</h3>
          <p className="info-text">
            Investing early is one of the smartest financial moves you can make. It allows your money to grow over time through compound interest.
          </p>
        </div>
        <div className="info-box">
          <h3 className="info-subtitle">How Compound Interest Works</h3>
          <p className="info-text">
            Compound interest means your investment earns interest on both the principal and the accumulated interest.
            Here’s the formula for an initial principal <strong>P</strong> with monthly contributions <strong>C</strong>:
          </p>
          <div className="formula-container">
            <p className="formula">
              A(t) = P ( 1 + <sup>r</sup>/<sub>n</sub> )<sup>n·t</sup> + C [ (1 + <sup>r</sup>/<sub>12</sub> )<sup>12·t</sup> – 1 ] / ( <sup>r</sup>/<sub>12</sub> )
            </p>
          </div>
          <div className="info-box-inner">
            <p><strong>P</strong>: Initial principal</p>
            <p><strong>r</strong>: Annual interest rate (decimal)</p>
            <p><strong>n</strong>: Compounding frequency for principal</p>
            <p><strong>t</strong>: Number of years</p>
            <p><strong>C</strong>: Monthly contribution</p>
          </div>
        </div>
        <div className="info-box">
          <h3 className="info-subtitle">Why Start Early?</h3>
          <div className="info-box-inner">
            <ul className="info-list">
              <li><strong>Exponential Growth:</strong> More time means more growth.</li>
              <li><strong>Risk Mitigation:</strong> A long-term approach smooths out volatility.</li>
              <li><strong>Compounding Magic:</strong> Earnings generate additional earnings over time.</li>
            </ul>
          </div>
        </div>
        <div className="info-box real-life-example">
          <h3 className="info-subtitle">Real-Life Example</h3>
          <div className="example-cards">
            <div className="example-card">
              <div className="image-green-header">Alice</div>
              <img src="/alice.jpeg" alt="Alice" className="example-card-image" />
              <p>
                Begins investing $200/month at age 25 with a 7% annual return (compounded monthly).
                By age 65, her investment grows to approximately <strong>$528,000</strong>.
              </p>
            </div>
            <div className="example-card">
              <div className="image-green-header">Bob</div>
              <img src="/bob.jpeg" alt="Bob" className="example-card-image" />
              <p>
                Starts at age 35 with the same $200/month contribution and 7% return.
                By age 65, his investment grows to roughly <strong>$245,000</strong>.
              </p>
            </div>
            <div className="example-card">
              <div className="image-green-header">Charlie</div>
              <img src="/charlie.jpeg" alt="Charlie" className="example-card-image" />
              <p>
                Begins investing $200/month at age 25 like Alice, but stops contributions at age 35.
                Despite no contributions after age 35, his investment grows to about <strong>$350,000</strong> by age 65,
                thanks to compound interest on his early contributions.
              </p>
            </div>
          </div>
          <p className="info-text">
            This example highlights not only the power of an early start but also the impact of consistency and duration on your final investment outcome.
          </p>
        </div>
        <div className="info-box">
          <h3 className="info-subtitle">Tips for Getting Started</h3>
          <div className="info-box-inner">
            <ul className="info-list">
              <li><strong>Start Small:</strong> Even modest amounts add up over time.</li>
              <li><strong>Be Consistent:</strong> Regular contributions maximize compounding benefits.</li>
              <li><strong>Diversify:</strong> Spread your investments to reduce risk.</li>
              <li><strong>Stay Patient:</strong> Think long term and avoid impulsive decisions during market downturns.</li>
            </ul>
          </div>
        </div>

        {/* NEW: Investment Platforms Section */}
        <div className="info-box deep-dive">
          <h3 className="info-subtitle">Best Investment Platforms</h3>
          <p className="info-text">
            Below are some popular online brokerage and investment platforms, with pros, cons, and links to learn more:
          </p>
          <ul className="platform-list">
            <li>
              <a href="https://www.fidelity.com" target="_blank" rel="noopener noreferrer">
                Fidelity
              </a>
              <p className="platform-pros"><strong>Pros:</strong> Comprehensive research tools, extensive mutual fund lineup, high‐quality customer service.</p>
              <p className="platform-cons"><strong>Cons:</strong> Some mutual funds carry transaction fees; the interface can be overwhelming for beginners.</p>
            </li>
            <li>
              <a href="https://www.robinhood.com" target="_blank" rel="noopener noreferrer">
                Robinhood
              </a>
              <p className="platform-pros"><strong>Pros:</strong> Commission‐free trades, easy mobile app, fractional shares.</p>
              <p className="platform-cons"><strong>Cons:</strong> Limited research tools, no mutual funds or bonds, occasional reliability issues.</p>
            </li>
            <li>
              <a href="https://www.tdameritrade.com" target="_blank" rel="noopener noreferrer">
                TD Ameritrade
              </a>
              <p className="platform-pros"><strong>Pros:</strong> Robust trading platform (thinkorswim), no commission on stocks/ETFs, excellent educational resources.</p>
              <p className="platform-cons"><strong>Cons:</strong> Platform may be complex for new users; some mutual funds carry fees.</p>
            </li>
            <li>
              <a href="https://www.schwab.com" target="_blank" rel="noopener noreferrer">
                Charles Schwab
              </a>
              <p className="platform-pros"><strong>Pros:</strong> No minimum account, wide range of low‐cost ETFs, strong customer service.</p>
              <p className="platform-cons"><strong>Cons:</strong> Advanced tools may have a learning curve; some mutual fund expense ratios are higher.</p>
            </li>
          </ul>
        </div>

        {/* NEW: Investment Vehicles Section */}
        <div className="info-box deep-dive">
          <h3 className="info-subtitle">Key Investment Vehicles</h3>
          <div className="vehicle-item">
            <h4>Roth IRA</h4>
            <p className="info-text">
              A Roth Individual Retirement Account allows you to contribute after-tax dollars. Earnings grow tax-free, and qualified withdrawals in retirement are tax-free.
            </p>
            <p className="vehicle-pros"><strong>Pros:</strong> Tax-free growth, no required minimum distributions, contributions can be withdrawn penalty-free.</p>
            <p className="vehicle-cons"><strong>Cons:</strong> Income limits restrict eligibility; contributions are not tax-deductible.</p>
          </div>
          <div className="vehicle-item">
            <h4>Mutual Fund</h4>
            <p className="info-text">
              A pooled investment vehicle managed by a professional. Investors buy shares in the fund, and the manager allocates money across stocks, bonds, or other assets.
            </p>
            <p className="vehicle-pros"><strong>Pros:</strong> Instant diversification, professional management, automatic reinvestment of dividends.</p>
            <p className="vehicle-cons"><strong>Cons:</strong> Management fees (expense ratios), potential load fees on purchase or sale, less control over individual holdings.</p>
          </div>
          <div className="vehicle-item">
            <h4>Hedge Fund</h4>
            <p className="info-text">
              A private investment partnership employing advanced strategies (leverage, short selling, derivatives) to generate returns. Typically available only to accredited investors.
            </p>
            <p className="vehicle-pros"><strong>Pros:</strong> Potential for high returns, access to sophisticated strategies, active risk management.</p>
            <p className="vehicle-cons"><strong>Cons:</strong> High fees (often “2 and 20”), illiquidity, high minimum investment, regulatory constraints.</p>
          </div>
          <div className="vehicle-item">
            <h4>Bonds</h4>
            <p className="info-text">
              Debt securities issued by corporations or governments. Bondholders receive periodic interest payments and return of principal at maturity.
            </p>
            <p className="vehicle-pros"><strong>Pros:</strong> Regular income (coupon payments), lower volatility than stocks, preservation of capital if held to maturity.</p>
            <p className="vehicle-cons"><strong>Cons:</strong> Interest rate risk (prices fall when rates rise), credit/default risk, lower long-term returns compared to equities.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

/* 
  5) Glossary Component 
  Opens as a separate page with a table of definitions.
*/
const Glossary = ({ onBack }) => {
  const glossaryData = [
    { term: "Stock", definition: "A share of ownership in a company." },
    { term: "Bond", definition: "A debt security representing a loan made by an investor to a borrower." },
    { term: "Compound Interest", definition: "Interest calculated on both the principal and the accumulated interest." },
    { term: "Diversification", definition: "Spreading investments to reduce risk." },
    { term: "Dividend", definition: "A portion of a company's earnings distributed to shareholders." },
    { term: "Portfolio", definition: "A collection of investments held by an individual or organization." },
    { term: "Market Capitalization", definition: "The total market value of a company's outstanding shares." },
    { term: "Mutual Fund", definition: "An investment vehicle that pools money from many investors to purchase securities." },
    { term: "ETF", definition: "An Exchange-Traded Fund that tracks an index and trades like a stock." },
    { term: "Index Fund", definition: "A fund designed to follow a market index." },
    { term: "Risk", definition: "The potential for losing money on an investment." },
    { term: "Return", definition: "The profit or loss made on an investment." },
    { term: "Asset", definition: "Anything valuable that is owned." },
    { term: "Liquidity", definition: "How quickly an asset can be converted into cash." },
    { term: "Volatility", definition: "The degree of variation in the price of a financial instrument over time." },
    { term: "Blue-Chip", definition: "Large, financially sound companies with a history of reliable performance." },
    { term: "Growth Stock", definition: "A stock expected to grow at an above-average rate." },
    { term: "Value Stock", definition: "A stock that appears undervalued based on fundamentals." },
    { term: "Market Order", definition: "An order to buy or sell a security immediately at the best available price." },
    { term: "Limit Order", definition: "An order to buy or sell a security at a specified price or better." },
    { term: "Capital Gain", definition: "The profit from selling an asset for more than its purchase price." },
    { term: "Reinvestment", definition: "Using dividends or interest to purchase additional shares." },
  ];

  return (
    <div className="glossary-page card">
      <h2 className="glossary-title">Glossary of Terms</h2>
      <table className="glossary-table">
        <thead>
          <tr>
            <th>Term</th>
            <th>Definition</th>
          </tr>
        </thead>
        <tbody>
          {glossaryData.map((item, index) => (
            <tr key={index}>
              <td>{item.term}</td>
              <td>{item.definition}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <button className="info-button" onClick={onBack}>
        Back to Simulator
      </button>
    </div>
  );
};

/* 
  6) InvestorQuiz Component 
  Fills entire viewport with a gradient from the body,
  plus a white "card" with green border around the quiz content.
*/
const InvestorQuiz = ({ onBack }) => {
  const questions = [
    {
      question: "How comfortable are you with risk?",
      options: [
        { text: "Very uncomfortable", score: 1 },
        { text: "Somewhat uncomfortable", score: 2 },
        { text: "Neutral", score: 3 },
        { text: "Comfortable", score: 4 },
      ],
    },
    {
      question: "How much do you value safety over returns?",
      options: [
        { text: "Safety is my top priority", score: 1 },
        { text: "I prefer moderate safety", score: 2 },
        { text: "I'm balanced", score: 3 },
        { text: "I sacrifice safety for high returns", score: 4 },
      ],
    },
    {
      question: "How long do you plan to invest?",
      options: [
        { text: "Less than 5 years", score: 1 },
        { text: "5-10 years", score: 2 },
        { text: "10-20 years", score: 3 },
        { text: "20+ years", score: 4 },
      ],
    },
    {
      question: "How do you react to market downturns?",
      options: [
        { text: "I panic and sell", score: 1 },
        { text: "I feel uneasy but hold", score: 2 },
        { text: "I stay calm", score: 3 },
        { text: "I see it as a buying opportunity", score: 4 },
      ],
    },
    {
      question: "How important is liquidity to you?",
      options: [
        { text: "Very important", score: 1 },
        { text: "Somewhat important", score: 2 },
        { text: "Not very important", score: 3 },
        { text: "Not important at all", score: 4 },
      ],
    },
    {
      question: "What is your primary investment goal?",
      options: [
        { text: "Preservation of capital", score: 1 },
        { text: "Steady income", score: 2 },
        { text: "Moderate growth", score: 3 },
        { text: "Aggressive growth", score: 4 },
      ],
    },
    {
      question: "How often do you review your investments?",
      options: [
        { text: "Rarely", score: 1 },
        { text: "Once a year", score: 2 },
        { text: "A few times a year", score: 3 },
        { text: "Regularly and actively", score: 4 },
      ],
    },
    {
      question: "How much do you know about investing terms?",
      options: [
        { text: "Not much", score: 1 },
        { text: "Some", score: 2 },
        { text: "A moderate amount", score: 3 },
        { text: "A lot", score: 4 },
      ],
    },
    {
      question: "Which best describes your investment style?",
      options: [
        { text: "Conservative", score: 1 },
        { text: "Cautious", score: 2 },
        { text: "Balanced", score: 3 },
        { text: "Aggressive", score: 4 },
      ],
    },
    {
      question: "How important is outperforming the market to you?",
      options: [
        { text: "Not important", score: 1 },
        { text: "Somewhat important", score: 2 },
        { text: "Important", score: 3 },
        { text: "Very important", score: 4 },
      ],
    },
  ];

  const totalQuestions = questions.length;
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [score, setScore] = useState(0);

  const handleOptionSelect = (optionScore) => {
    const newAnswers = [...answers, optionScore];
    setAnswers(newAnswers);

    if (currentQuestion + 1 < totalQuestions) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      const totalScore = newAnswers.reduce((a, b) => a + b, 0);
      setScore(totalScore);
      setQuizCompleted(true);
    }
  };

  const progressPercentage = Math.round(
    ((currentQuestion + (quizCompleted ? 1 : 0)) / totalQuestions) * 100
  );

  return (
    <div className="quiz-container">
      <div className="quiz-inner">
        <h2 className="quiz-title">Investor Personality Quiz</h2>
        {!quizCompleted ? (
          <>
            <div className="quiz-progress-bar">
              <div
                className="quiz-progress"
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>
            <div className="quiz-question">
              <p>{questions[currentQuestion].question}</p>
            </div>
            <div className="quiz-options">
              {questions[currentQuestion].options.map((option, index) => (
                <button
                  key={index}
                  className="quiz-option"
                  onClick={() => handleOptionSelect(option.score)}
                >
                  {option.text}
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="quiz-completion-card">
              <h3 className="completion-title">🎉 Quiz Completed!</h3>
              <p className="completion-score">Your total score is: <span>{score}</span></p>
              <p className="completion-feedback">
                {score <= 15
                  ? "You have a very conservative investment style."
                  : score <= 25
                  ? "You have a cautious investment style."
                  : score <= 35
                  ? "You have a balanced investment style."
                  : "You have an aggressive investment style."}
              </p>
              <button className="info-button completion-back" onClick={onBack}>
                Back to Simulator
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/* 
  7) BudgetPlanner Component 
  Includes a Savings Goal Calculator and a Budget Allocation Guide
*/
const BudgetPlanner = ({ onBack }) => {
  const [currentSavingsStr, setCurrentSavingsStr] = useState("");
  const [goalStr, setGoalStr] = useState("");
  const [monthlySavingsStr, setMonthlySavingsStr] = useState("");
  const [annualRateStr, setAnnualRateStr] = useState("");
  const [goalResult, setGoalResult] = useState("");

  const calculateMonthsToGoal = () => {
    const currentSavings = Number(currentSavingsStr) || 0;
    const goal = Number(goalStr) || 0;
    const monthlySavings = Number(monthlySavingsStr) || 0;
    const annualRate = Number(annualRateStr) || 0;

    let months = 0;
    let balance = currentSavings;
    const monthlyRate = annualRate / 12;
    while (balance < goal && months < 1000) {
      balance = balance * (1 + monthlyRate) + monthlySavings;
      months++;
    }
    if (months >= 1000) {
      setGoalResult("Goal not achievable within a reasonable time frame.");
    } else {
      const years = Math.floor(months / 12);
      const remainingMonths = months % 12;
      setGoalResult(
        `It will take approximately ${years} years and ${remainingMonths} months to reach your goal.`
      );
    }
  };

  // For each input, strip leading zeros onChange
  const handleCurrentSavingsChange = (e) => {
    const stripped = e.target.value.replace(/^0+/, "");
    setCurrentSavingsStr(stripped);
  };
  const handleGoalChange = (e) => {
    const stripped = e.target.value.replace(/^0+/, "");
    setGoalStr(stripped);
  };
  const handleMonthlySavingsChange = (e) => {
    const stripped = e.target.value.replace(/^0+/, "");
    setMonthlySavingsStr(stripped);
  };
  const handleAnnualRateChange = (e) => {
    const stripped = e.target.value.replace(/^0+/, "");
    setAnnualRateStr(stripped);
  };

  return (
    <div className="budget-planner-page card">
      <h2 className="budget-planner-title">Budget & Savings Planner</h2>

      {/* Savings Goal Calculator Section */}
      <div className="budget-section">
        <h3 className="budget-section-title">Savings Goal Calculator</h3>
        <div className="budget-form">
          <label>Current Savings ($):</label>
          <input
            type="number"
            min="0"
            value={currentSavingsStr}
            onChange={handleCurrentSavingsChange}
            placeholder="e.g. 1000"
          />
          <label>Savings Goal ($):</label>
          <input
            type="number"
            min="0"
            value={goalStr}
            onChange={handleGoalChange}
            placeholder="e.g. 10000"
          />
          <label>Monthly Savings ($):</label>
          <input
            type="number"
            min="0"
            value={monthlySavingsStr}
            onChange={handleMonthlySavingsChange}
            placeholder="e.g. 200"
          />
          <label>Annual Interest Rate (decimal):</label>
          <input
            type="number"
            min="0"
            value={annualRateStr}
            step="0.01"
            onChange={handleAnnualRateChange}
            placeholder="e.g. 0.05"
          />
          <button className="info-button" onClick={calculateMonthsToGoal}>
            Calculate Goal Time
          </button>
        </div>
        {goalResult && <p className="budget-result">{goalResult}</p>}
      </div>

      {/* Budget Allocation Guide Section */}
      <div className="budget-section">
        <h3 className="budget-section-title">Budget Allocation Guide</h3>
        <div className="budget-guide">
          <p>
            A well-planned budget helps you balance your income and expenses while saving for the future. 
            One popular guideline is the <strong>50/30/20 rule</strong>:
          </p>
          <ul>
            <li>
              <strong>50% for Needs:</strong> Allocate half of your income for essential expenses 
              (e.g., housing, food, and transportation).
            </li>
            <li>
              <strong>30% for Wants:</strong> Use about 30% of your income for non-essential items 
              (e.g., entertainment, dining out, or hobbies).
            </li>
            <li>
              <strong>20% for Savings:</strong> Reserve 20% of your income for savings, investments, 
              or paying off debt.
            </li>
          </ul>
          <p>
            Keep in mind that these percentages are guidelines, not strict rules. Everyone’s financial 
            situation is different. If your “needs” exceed 50% of your income, you may need to reduce 
            your “wants” category or find ways to cut essential costs. If you want to save aggressively, 
            you can increase the savings percentage.
          </p>
          <p>
            The most important step is to <strong>track your spending</strong> and regularly review your budget. 
            This helps you spot areas where you can adjust and ensures you’re setting aside enough for 
            your long-term goals. Consistency and awareness are key to successful budgeting!
          </p>
        </div>
      </div>

      <button className="info-button budget-back-button" onClick={onBack}>
        Back to Simulator
      </button>
    </div>
  );
};

/* 
  8) CombinedGraph: plots two scenarios on a single SVG
     Now also shows final savings for each scenario below the SVG.
*/
const CombinedGraph = ({ scenarios }) => {
  // Convert each scenario’s strings → numbers
  const allData = scenarios.map((s) => {
    const P = Number(s.principalStr) || 0;
    const r = Number(s.rateStr) || 0;
    const t = Number(s.yearsStr) || 0;
    const n = Number(s.compoundingsStr) || 1;
    const C = Number(s.monthlyContributionStr) || 0;
    return calculateCompoundInterest(P, r, t, n, C);
  });
  const xMax = Math.max(...scenarios.map((s) => Number(s.yearsStr) || 0));
  const yMax = allData.reduce((max, dataArr) => {
    return Math.max(max, dataArr.reduce((m2, pt) => (pt.y > m2 ? pt.y : m2), 0));
  }, 0);

  // final savings for each scenario
  const finalA = allData[0]?.[allData[0].length - 1].y.toFixed(2) || "0.00";
  const finalB = allData[1]?.[allData[1].length - 1].y.toFixed(2) || "0.00";

  return (
    <div className="graph-container card">
      <h3 className="graph-title">Scenario Comparison</h3>
      <svg width="700" height="350" viewBox="-50 -50 600 400">
        {/* Vertical grid lines and x‐axis labels */}
        {[...Array(6)].map((_, i) => (
          <g key={`v-${i}`}>
            <line x1={i * 100} y1="0" x2={i * 100} y2="300" stroke="#ddd" />
            <text x={i * 100} y="320" fontSize="12" textAnchor="middle">
              {Math.round((i / 5) * xMax)}
            </text>
          </g>
        ))}
        {/* Horizontal grid lines and y‐axis labels */}
        {[...Array(6)].map((_, i) => (
          <g key={`h-${i}`}>
            <line x1="0" y1={i * 50} x2="500" y2={i * 50} stroke="#ddd" />
            <text x="-20" y={300 - i * 50} fontSize="12" textAnchor="end">
              {Math.round((i / 5) * yMax)}
            </text>
          </g>
        ))}
        {/* Axes */}
        <line x1="0" y1="300" x2="500" y2="300" stroke="#333" strokeWidth="2" />
        <line x1="0" y1="0" x2="0" y2="300" stroke="#333" strokeWidth="2" />
        {/* Axis labels */}
        <text x="250" y="350" fontSize="14" textAnchor="middle">
          Years Invested
        </text>
        <text
          x="-70"
          y="100"
          fontSize="14"
          textAnchor="middle"
          transform="rotate(-90,-50,125)"
        >
          Total Savings ($)
        </text>
        {/* Polylines for each scenario */}
        {allData.map((dataArr, idx) => (
          <polyline
            key={`poly-${idx}`}
            fill="none"
            stroke={scenarios[idx].color}
            strokeWidth="3"
            points={dataArr
              .map(
                ({ x, y }) =>
                  `${(x / xMax) * 500},${300 - (y / yMax) * 300}`
              )
              .join(" ")}
          />
        ))}
      </svg>

      {/* Legend */}
      <div className="legend-container">
        {scenarios.map((s, idx) => (
          <div key={`leg-${idx}`} className="legend-item">
            <span
              className="legend-color-box"
              style={{ backgroundColor: s.color }}
            ></span>
            <span className="legend-label">{s.label}</span>
          </div>
        ))}
      </div>

      {/* Final Savings Display */}
      <div className="comparison-final">
        <p>
          <strong>Scenario A Final Savings:</strong> ${finalA}
        </p>
        <p>
          <strong>Scenario B Final Savings:</strong> ${finalB}
        </p>
      </div>
    </div>
  );
};

/* 
  9) CombinedDesmos: interactive Desmos view for two scenarios
*/
const CombinedDesmos = ({ scenarios }) => {
  const calculatorRef = useRef(null);
  const calculator = useRef(null);

  useEffect(() => {
    if (!calculator.current && window.Desmos) {
      calculator.current = window.Desmos.GraphingCalculator(calculatorRef.current, {
        expressions: true,
        expressionsCollapsed: true,
        settingsMenu: false,
      });
      calculator.current.updateSettings({ expressionsCollapsed: true });
    }
    if (calculator.current) {
      scenarios.forEach((s, idx) => {
        const P = Number(s.principalStr) || 0;
        const r = Number(s.rateStr) || 0;
        const t = Number(s.yearsStr) || 0;
        const n = Number(s.compoundingsStr) || 1;
        const C = Number(s.monthlyContributionStr) || 0;

        const exprId = `compound${idx}`;
        const latex = `A_${idx}(t) = ${P} \\left(1+\\frac{${r}}{${n}}\\right)^{${n} t} + ${C} \\left(\\frac{\\left(1+\\frac{${r}}{12}\\right)^{12 t} - 1}{${r}/12}\\right)`;
        calculator.current.setExpression({
          id: exprId,
          latex,
          color: s.color,
          lineWidth: 2,
        });
      });
      const maxYears = Math.max(...scenarios.map((s) => Number(s.yearsStr) || 0));
      const finalYs = scenarios.map((s) => {
        const P = Number(s.principalStr) || 0;
        const r = Number(s.rateStr) || 0;
        const t = Number(s.yearsStr) || 0;
        const n = Number(s.compoundingsStr) || 1;
        const C = Number(s.monthlyContributionStr) || 0;
        const pg = P * Math.pow(1 + r / n, n * t);
        const cg =
          C * ((Math.pow(1 + r / 12, 12 * t) - 1) / (r / 12));
        return pg + cg;
      });
      const maxY = Math.max(...finalYs);
      calculator.current.setMathBounds({
        left: 0,
        right: maxYears <= 0 ? 10 : maxYears,
        bottom: 0,
        top: maxY > 0 ? maxY * 1.1 : 1000,
      });
    }
  }, [scenarios]);

  return (
    <div className="desmos-container card">
      <h3 className="graph-title">Desmos Interactive Comparison</h3>
      <div ref={calculatorRef} style={{ width: "900px", height: "450px", margin: "0 auto" }} />
    </div>
  );
};

/* 
  10) YearlyBreakdownComparison:
      Two side‐by‐side tables, one per scenario
*/
const YearlyBreakdownComparison = ({ scenarios }) => {
  return (
    <div className="yearly-breakdown-comparison">
      {scenarios.map((s, idx) => {
        const P = Number(s.principalStr) || 0;
        const r = Number(s.rateStr) || 0;
        const t = Number(s.yearsStr) || 0;
        const n = Number(s.compoundingsStr) || 1;
        const C = Number(s.monthlyContributionStr) || 0;

        const breakdown = [];
        for (let i = 0; i <= t; i++) {
          const principalGrowth = P * Math.pow(1 + r / n, n * i);
          const contributionsGrowth =
            C * ((Math.pow(1 + r / 12, 12 * i) - 1) / (r / 12));
          breakdown.push({
            year: i,
            principalGrowth,
            contributionsGrowth,
            total: principalGrowth + contributionsGrowth,
          });
        }
        return (
          <div key={`breakdown-${idx}`} className="yearly-breakdown card">
            <h3 className="breakdown-title">{s.label} Year‐by‐Year</h3>
            <table className="breakdown-table">
              <thead>
                <tr>
                  <th>Year</th>
                  <th>Principal Growth ($)</th>
                  <th>Contributions ($)</th>
                  <th>Total ($)</th>
                </tr>
              </thead>
              <tbody>
                {breakdown.map((item) => (
                  <tr key={item.year}>
                    <td>{item.year}</td>
                    <td>{item.principalGrowth.toFixed(2)}</td>
                    <td>{item.contributionsGrowth.toFixed(2)}</td>
                    <td>{item.total.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
};

/* 
  11) ChallengeQuiz Component 
  “If you invest P for T years at r% (compounded annually), what's the balance?”
  Now includes an explanation below the title, a coin counter, and prevents negative input.
*/
const ChallengeQuiz = ({ onBack }) => {
  const [principal, setPrincipal] = useState(0);
  const [rate, setRate] = useState(0);
  const [years, setYears] = useState(0);
  const [userAnswer, setUserAnswer] = useState("");
  const [correctAnswer, setCorrectAnswer] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [coinCount, setCoinCount] = useState(0); // track number of correct answers

  const newQuestion = () => {
    const P = Math.floor(100 + Math.random() * 900); // 100–999
    const r = parseFloat((0.02 + Math.random() * 0.08).toFixed(3)); // 0.02–0.10
    const t = Math.floor(1 + Math.random() * 19); // 1–19 years
    setPrincipal(P);
    setRate(r);
    setYears(t);
    setUserAnswer("");
    setFeedback("");
    const correct = Math.round(P * Math.pow(1 + r / 1, 1 * t));
    setCorrectAnswer(correct);
  };

  useEffect(() => {
    newQuestion();
  }, []);

  const handleAnswerChange = (e) => {
    // Strip non-digits (no negatives, no decimals): only allow [0-9]
    const digitsOnly = e.target.value.replace(/\D/g, "");
    setUserAnswer(digitsOnly);
  };

  const handleSubmit = () => {
    const numeric = parseInt(userAnswer, 10);
    if (!isNaN(numeric)) {
      if (numeric === correctAnswer) {
        setFeedback("🎉 Correct!");
        setCoinCount((prev) => prev + 1);
      } else {
        setFeedback(`❌ Oops—correct answer is $${correctAnswer}`);
      }
    } else {
      setFeedback("Please enter a valid number.");
    }
  };

  return (
    <div className="challenge-container">
      <div className="challenge-card">
        <h2 className="challenge-title">Calculator Challenge</h2>
        <p className="coin-count">🪙 Coins: {coinCount}</p>
        <div className="challenge-explanation">
          <p>
            <strong>How it’s calculated:</strong> To find the compounded balance, use 
            <em> A = P × (1 + r)<sup>t</sup> </em>, where P is principal, r is the annual rate (decimal), and t is years. Then round to the nearest dollar.
          </p>
        </div>
        <p className="challenge-question">
          If you invest <strong>${principal}</strong> for <strong>{years} years</strong> at{" "}
          <strong>{(rate * 100).toFixed(1)}%</strong> (compounded annually), what’s the balance? (round to nearest dollar)
        </p>
        <div className="challenge-input-group">
          <input
            type="number"
            min="0"
            value={userAnswer}
            onChange={handleAnswerChange}
            placeholder="Enter your answer"
          />
          <button className="challenge-button" onClick={handleSubmit}>
            Submit
          </button>
        </div>
        {feedback && <p className="challenge-feedback">{feedback}</p>}
        <div className="challenge-buttons">
          <button className="challenge-button" onClick={newQuestion}>
            New Question
          </button>
          <button className="challenge-button" onClick={onBack}>
            Back to Simulator
          </button>
        </div>
      </div>
    </div>
  );
};

/* 
  12) Main App Component 
  Uses conditional rendering for:
    - SingleSimulator
    - ScenarioComparison
    - CalculatorChallenge
    - InfoPage
    - Glossary
    - InvestorQuiz
    - BudgetPlanner
*/
const App = () => {
  // Top-level “view” state
  const [view, setView] = useState("single");

  /* Single Simulator state (all as strings) */
  const [principalStr, setPrincipalStr] = useState("1000");
  const [rateStr, setRateStr] = useState("0.07");
  const [yearsStr, setYearsStr] = useState("10");
  const [compoundingsStr, setCompoundingsStr] = useState("12");
  const [monthlyContributionStr, setMonthlyContributionStr] = useState("");

  /* Scenario A state (strings) */
  const [principalAStr, setPrincipalAStr] = useState("1000");
  const [rateAStr, setRateAStr] = useState("0.07");
  const [yearsAStr, setYearsAStr] = useState("10");
  const [compoundingsAStr, setCompoundingsAStr] = useState("12");
  const [monthlyContributionAStr, setMonthlyContributionAStr] = useState("");

  /* Scenario B state (strings) */
  const [principalBStr, setPrincipalBStr] = useState("1000");
  const [rateBStr, setRateBStr] = useState("0.05");
  const [yearsBStr, setYearsBStr] = useState("10");
  const [compoundingsBStr, setCompoundingsBStr] = useState("12");
  const [monthlyContributionBStr, setMonthlyContributionBStr] = useState("");

  /* Popular presets for single simulator */
  const popularRates = [
    { label: "Savings (0.5%)", value: "0.005" },
    { label: "Bonds (3%)", value: "0.03" },
    { label: "Stocks (7%)", value: "0.07" },
    { label: "High (10%)", value: "0.1" },
  ];
  const compoundingFrequencies = [
    { label: "Annually", value: "1" },
    { label: "Quarterly", value: "4" },
    { label: "Monthly", value: "12" },
    { label: "Daily", value: "365" },
  ];
  const [customRateSelected, setCustomRateSelected] = useState(false);
  const [customCompoundingSelected, setCustomCompoundingSelected] = useState(false);
  const [showRateHelp, setShowRateHelp] = useState(false);
  const [showFreqHelp, setShowFreqHelp] = useState(false);

  /* Handlers that strip leading zeros */
  const handlePrincipalChange = (e) => {
    setPrincipalStr(e.target.value.replace(/^0+/, ""));
  };
  const handleRateChange = (e) => {
    setRateStr(e.target.value.replace(/^0+/, ""));
  };
  const handleYearsChange = (e) => {
    setYearsStr(e.target.value.replace(/^0+/, ""));
  };
  const handleCompoundingsChange = (e) => {
    setCompoundingsStr(e.target.value.replace(/^0+/, ""));
  };
  const handleMonthlyContributionChange = (e) => {
    setMonthlyContributionStr(e.target.value.replace(/^0+/, ""));
  };

  /* Scenario A handlers */
  const handlePrincipalAChange = (e) => {
    setPrincipalAStr(e.target.value.replace(/^0+/, ""));
  };
  const handleRateAChange = (e) => {
    setRateAStr(e.target.value.replace(/^0+/, ""));
  };
  const handleYearsAChange = (e) => {
    setYearsAStr(e.target.value.replace(/^0+/, ""));
  };
  const handleCompoundingsAChange = (e) => {
    setCompoundingsAStr(e.target.value.replace(/^0+/, ""));
  };
  const handleMonthlyContributionAChange = (e) => {
    setMonthlyContributionAStr(e.target.value.replace(/^0+/, ""));
  };

  /* Scenario B handlers */
  const handlePrincipalBChange = (e) => {
    setPrincipalBStr(e.target.value.replace(/^0+/, ""));
  };
  const handleRateBChange = (e) => {
    setRateBStr(e.target.value.replace(/^0+/, ""));
  };
  const handleYearsBChange = (e) => {
    setYearsBStr(e.target.value.replace(/^0+/, ""));
  };
  const handleCompoundingsBChange = (e) => {
    setCompoundingsBStr(e.target.value.replace(/^0+/, ""));
  };
  const handleMonthlyContributionBChange = (e) => {
    setMonthlyContributionBStr(e.target.value.replace(/^0+/, ""));
  };

  /* Prepare scenarios array for comparison (all string‐based) */
  const scenarios = [
    {
      principalStr: principalAStr,
      rateStr: rateAStr,
      yearsStr: yearsAStr,
      compoundingsStr: compoundingsAStr,
      monthlyContributionStr: monthlyContributionAStr,
      label: "Scenario A",
      color: "#FF5733",
    },
    {
      principalStr: principalBStr,
      rateStr: rateBStr,
      yearsStr: yearsBStr,
      compoundingsStr: compoundingsBStr,
      monthlyContributionStr: monthlyContributionBStr,
      label: "Scenario B",
      color: "#3355FF",
    },
  ];

  /* RENDER LOGIC */
  if (view === "info") {
    return (
      <div className="app-container">
        <header className="app-header">
          <img
            src="/Time To Invest.png"
            alt="Investing Banner"
            className="header-image with-border"
          />
          <h1 className="title">The Power of Investing Early</h1>
          <div className="header-buttons">
            <button className="info-button" onClick={() => setView("single")}>
              Back to Simulator
            </button>
            <button className="info-button" onClick={() => setView("glossary")}>
              Glossary of Terms
            </button>
            <button className="info-button" onClick={() => setView("quiz")}>
              Investor Personality Quiz
            </button>
            <button className="info-button" onClick={() => setView("budget")}>
              Budget Planner
            </button>
            <button className="info-button" onClick={() => setView("scenario")}>
              Scenario Comparison
            </button>
          </div>
        </header>
        <InfoPage />
      </div>
    );
  }

  if (view === "glossary") {
    return <Glossary onBack={() => setView("single")} />;
  }

  if (view === "quiz") {
    return <InvestorQuiz onBack={() => setView("single")} />;
  }

  if (view === "budget") {
    return <BudgetPlanner onBack={() => setView("single")} />;
  }

  if (view === "challenge") {
    return <ChallengeQuiz onBack={() => setView("single")} />;
  }

  if (view === "scenario") {
    return (
      <div className="app-container">
        <header className="app-header">
          <img
            src="/Time To Invest.png"
            alt="Investing Banner"
            className="header-image with-border"
          />
          <h1 className="title">Scenario Comparison Simulator</h1>
          <div className="header-buttons">
            <button className="info-button" onClick={() => setView("single")}>
              Single Simulator
            </button>
            <button className="info-button" onClick={() => setView("challenge")}>
              Calculator Challenge
            </button>
            <button className="info-button" onClick={() => setView("info")}>
              Why Investing Matters
            </button>
            <button className="info-button" onClick={() => setView("glossary")}>
              Glossary
            </button>
            <button className="info-button" onClick={() => setView("quiz")}>
              Investor Quiz
            </button>
            <button className="info-button" onClick={() => setView("budget")}>
              Budget Planner
            </button>
          </div>
        </header>

        {/* Two Input Columns for Scenario A & B */}
        <div className="comparison-input-group">
          {/* Scenario A Inputs */}
          <div className="input-container scenario-input">
            <h3 className="scenario-title">Scenario A</h3>
            <label>Starting Investment ($):</label>
            <input
              type="number"
              min="0"
              value={principalAStr}
              onChange={handlePrincipalAChange}
              placeholder="1000"
            />
            <label>Annual Growth Rate (decimal):</label>
            <input
              type="number"
              min="0"
              value={rateAStr}
              step="0.001"
              onChange={handleRateAChange}
              placeholder="0.07"
            />
            <label>Years Invested:</label>
            <input
              type="number"
              min="0"
              value={yearsAStr}
              onChange={handleYearsAChange}
              placeholder="10"
            />
            <label>Compounding Frequency (per year):</label>
            <input
              type="number"
              min="1"
              value={compoundingsAStr}
              onChange={handleCompoundingsAChange}
              placeholder="12"
            />
            <label>Monthly Contribution ($):</label>
            <input
              type="number"
              min="0"
              value={monthlyContributionAStr}
              onChange={handleMonthlyContributionAChange}
              placeholder="200"
            />
          </div>

          {/* Scenario B Inputs */}
          <div className="input-container scenario-input">
            <h3 className="scenario-title">Scenario B</h3>
            <label>Starting Investment ($):</label>
            <input
              type="number"
              min="0"
              value={principalBStr}
              onChange={handlePrincipalBChange}
              placeholder="1000"
            />
            <label>Annual Growth Rate (decimal):</label>
            <input
              type="number"
              min="0"
              value={rateBStr}
              step="0.001"
              onChange={handleRateBChange}
              placeholder="0.05"
            />
            <label>Years Invested:</label>
            <input
              type="number"
              min="0"
              value={yearsBStr}
              onChange={handleYearsBChange}
              placeholder="10"
            />
            <label>Compounding Frequency (per year):</label>
            <input
              type="number"
              min="1"
              value={compoundingsBStr}
              onChange={handleCompoundingsBChange}
              placeholder="12"
            />
            <label>Monthly Contribution ($):</label>
            <input
              type="number"
              min="0"
              value={monthlyContributionBStr}
              onChange={handleMonthlyContributionBChange}
              placeholder="200"
            />
          </div>
        </div>

        {/* Combined SVG Graph */}
        <CombinedGraph scenarios={scenarios} />

        {/* Combined Desmos Graph */}
        <CombinedDesmos scenarios={scenarios} />

        {/* Yearly Breakdown Comparison */}
        <YearlyBreakdownComparison scenarios={scenarios} />
      </div>
    );
  }

  /* Default: Single Simulator */
  return (
    <div className="app-container">
      <header className="app-header">
        <img
          src="/Time To Invest.png"
          alt="Investing Banner"
          className="header-image with-border"
        />
        <h1 className="title">The Power of Investing Early</h1>
        <div className="header-buttons">
          <button className="info-button" onClick={() => setView("scenario")}>
            Scenario Comparison
          </button>
          <button className="info-button" onClick={() => setView("challenge")}>
            Calculator Challenge
          </button>
          <button className="info-button" onClick={() => setView("info")}>
            Why Investing Matters
          </button>
          <button className="info-button" onClick={() => setView("glossary")}>
            Glossary of Terms
          </button>
          <button className="info-button" onClick={() => setView("quiz")}>
            Investor Personality Quiz
          </button>
          <button className="info-button" onClick={() => setView("budget")}>
            Budget Planner
          </button>
        </div>
      </header>

      {/* Input Section (Single Simulator) */}
      <div className="input-group">
        <div className="input-container">
          <label>Starting Investment ($):</label>
          <input
            type="number"
            min="0"
            value={principalStr}
            onChange={handlePrincipalChange}
            placeholder="1000"
          />
        </div>
        <div className="input-container">
          <label>Years Invested:</label>
          <input
            type="number"
            min="0"
            value={yearsStr}
            onChange={handleYearsChange}
            placeholder="10"
          />
        </div>
        <div className="input-container">
          <label>Monthly Contribution ($):</label>
          <input
            type="number"
            min="0"
            value={monthlyContributionStr}
            onChange={handleMonthlyContributionChange}
            placeholder="200"
          />
        </div>
      </div>

      {/* Selection Section (Single Simulator) */}
      <div className="selection-group">
        <div className="selection-container">
          <div className="selection-header">
            <div
              className="selection-info-icon"
              onClick={() => setShowRateHelp(true)}
              title="What is Growth Rate?"
            >
              ?
            </div>
            <h3>Select Growth Rate:</h3>
          </div>
          <div className="rate-buttons">
            {popularRates.map((rateOption) => (
              <button
                key={rateOption.label}
                className={`rate-button ${
                  !customRateSelected && rateStr === rateOption.value
                    ? "active"
                    : ""
                }`}
                onClick={() => {
                  setCustomRateSelected(false);
                  setRateStr(rateOption.value);
                }}
              >
                {rateOption.label}
              </button>
            ))}
            {!customRateSelected && (
              <button
                className="rate-button custom-button"
                onClick={() => setCustomRateSelected(true)}
              >
                Custom
              </button>
            )}
          </div>
          {customRateSelected && (
            <div className="custom-input">
              <input
                type="number"
                min="0"
                placeholder="Enter custom rate (e.g., 0.075)"
                value={rateStr}
                onChange={handleRateChange}
                step="0.001"
              />
            </div>
          )}
        </div>
        <div className="selection-container">
          <div className="selection-header">
            <div
              className="selection-info-icon"
              onClick={() => setShowFreqHelp(true)}
              title="What is Compounding Frequency?"
            >
              ?
            </div>
            <h3>Select Compounding Frequency:</h3>
          </div>
          <div className="frequency-buttons">
            {compoundingFrequencies.map((freq) => (
              <button
                key={freq.label}
                className={`frequency-button ${
                  !customCompoundingSelected &&
                  compoundingsStr === freq.value
                    ? "active"
                    : ""
                }`}
                onClick={() => {
                  setCustomCompoundingSelected(false);
                  setCompoundingsStr(freq.value);
                }}
              >
                {freq.label}
              </button>
            ))}
            {!customCompoundingSelected && (
              <button
                className="frequency-button custom-button"
                onClick={() => setCustomCompoundingSelected(true)}
              >
                Custom
              </button>
            )}
          </div>
          {customCompoundingSelected && (
            <div className="custom-input">
              <input
                type="number"
                min="1"
                placeholder="Enter custom frequency (e.g., 24)"
                value={compoundingsStr}
                onChange={handleCompoundingsChange}
                step="1"
              />
            </div>
          )}
        </div>
      </div>

      {/* Graphs */}
      <CompoundInterestGraph
        principalStr={principalStr}
        rateStr={rateStr}
        yearsStr={yearsStr}
        compoundingsStr={compoundingsStr}
        monthlyContributionStr={monthlyContributionStr}
        title="Your Investment Growth"
      />
      <DesmosGraph
        principalStr={principalStr}
        rateStr={rateStr}
        yearsStr={yearsStr}
        compoundingsStr={compoundingsStr}
        monthlyContributionStr={monthlyContributionStr}
        title="Desmos Interactive View"
      />

      {/* Year-by-Year Breakdown */}
      <YearlyBreakdown
        principalStr={principalStr}
        rateStr={rateStr}
        yearsStr={yearsStr}
        compoundingsStr={compoundingsStr}
        monthlyContributionStr={monthlyContributionStr}
      />

      {/* Pop-ups for Growth Rate and Frequency */}
      {showRateHelp && (
        <div className="popup-overlay" onClick={() => setShowRateHelp(false)}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <h4>What is Growth Rate?</h4>
            <p>
              The Growth Rate represents the annual return on your investment, expressed as a decimal.
              For example, a growth rate of 0.07 means a 7% annual return.
            </p>
            <button className="close-button" onClick={() => setShowRateHelp(false)}>
              Close
            </button>
          </div>
        </div>
      )}
      {showFreqHelp && (
        <div className="popup-overlay" onClick={() => setShowFreqHelp(false)}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()}>
            <h4>What is Compounding Frequency?</h4>
            <p>
              The Compounding Frequency indicates how many times per year your interest is compounded.
              For example, a frequency of 12 means the interest is compounded monthly.
            </p>
            <button className="close-button" onClick={() => setShowFreqHelp(false)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
